// Filtro de tarjetas OneClick por código de comercio de la máquina
// seleccionada. Módulo puro (sin React/Zustand/Sentry), mismo patrón que
// src/lib/mpCheckout.js y src/lib/mpReturn.js: testeable en aislamiento y
// prohibido mockearlo en los tests de los componentes que lo consumen.
//
// Motivo: un token OneClick (`tbk_user`) sólo es válido para el
// `codigo_comercio` bajo el cual fue inscrito. Mostrar/usar una tarjeta de
// otro comercio garantiza un 422 de Transbank
// ("InkToken ... not found or inactive for commerce ...").

// Válvula de emergencia: si el filtro resultara demasiado estricto en
// producción (p. ej. muchas tarjetas heredadas sin `codigo_comercio`, ver
// P4), poner esta constante en `false` hace que `filtrarTarjetasPorComercio`
// devuelva la lista deduplicada sin filtrar por comercio, sin revertir el
// resto del cambio. Análogo a `MP_TOTAL_TOLERANCIA = Infinity` en
// mpCheckout.js.
export const ONECLICK_FILTRO_ACTIVO = true;

export const MSG_TARJETA_OTRO_COMERCIO =
  "La tarjeta guardada no es válida para esta tienda, agrega una tarjeta nueva";

export const MSG_SIN_TARJETAS_TIENDA =
  "Tus tarjetas guardadas son de otra tienda. Agrega una tarjeta para esta compra.";

/**
 * normalizarComercio(valor)
 * `codigo_comercio` puede llegar como número (BD) o string (env). Normaliza
 * a string recortado; cualquier valor ausente/vacío/los literales "null",
 * "undefined", "NaN" (que a veces aparecen serializados) se tratan como "".
 */
export const normalizarComercio = (valor) => {
  if (valor === null || valor === undefined) return "";
  const str = String(valor).trim();
  if (str === "" || str === "null" || str === "undefined" || str === "NaN") {
    return "";
  }
  return str;
};

/**
 * resolverComercioEfectivo({ appEnv, codigoComercioDev, codigoComercioMachine })
 * Replica exactamente la regla usada hoy en AddCardOneClick.jsx / CardOneClick.jsx:
 * en development/testing se usa el código de comercio de integración
 * (VITE_CODE_COMERCE_DEV); en cualquier otro ambiente (producción), el de la
 * máquina seleccionada. El env se inyecta (nunca se lee `import.meta.env`
 * dentro de este módulo) para mantenerlo puro y testeable con `vi.stubEnv`.
 */
export const resolverComercioEfectivo = ({
  appEnv,
  codigoComercioDev,
  codigoComercioMachine,
}) => {
  const esDev = appEnv === "development" || appEnv === "testing";
  return normalizarComercio(esDev ? codigoComercioDev : codigoComercioMachine);
};

/**
 * esTarjetaValidaParaComercio(card, comercioEfectivo)
 * Criterio 7: una tarjeta sin `codigo_comercio` (dato heredado) se trata
 * como NO válida (falla segura: se sabe que una tarjeta de otro comercio
 * falla con 422 garantizado; excluirla sólo cuesta re-inscribir la tarjeta).
 * Nunca hay match `"" === ""` (comercio efectivo vacío + tarjeta sin
 * comercio): la comparación exige que ambos valores normalizados sean
 * iguales Y no vacíos.
 */
export const esTarjetaValidaParaComercio = (card, comercioEfectivo) => {
  const comercio = normalizarComercio(comercioEfectivo);
  if (comercio === "") return false;
  if (!card?.tbk_user) return false;

  const comercioTarjeta = normalizarComercio(card?.codigo_comercio);
  if (comercioTarjeta === "") return false;

  return comercioTarjeta === comercio;
};

/**
 * filtrarTarjetasPorComercio(cards, comercioEfectivo)
 * Filtra + deduplica por `codigo_comercio + tbk_user` (misma clave que
 * WalletForm.jsx), reemplazando el `_.uniqBy(cards, "tbk_user")` que dejaba
 * visibles dos tarjetas con el mismo `card_number` cuando la misma tarjeta
 * física estaba inscrita en dos comercios.
 */
export const filtrarTarjetasPorComercio = (cards, comercioEfectivo) => {
  if (!Array.isArray(cards)) return [];
  if (!ONECLICK_FILTRO_ACTIVO) {
    return dedupCards(cards.filter(Boolean));
  }

  const comercio = normalizarComercio(comercioEfectivo);
  if (comercio === "") return [];

  const validas = cards
    .filter(Boolean)
    .filter((card) => esTarjetaValidaParaComercio(card, comercio));

  return dedupCards(validas);
};

const dedupCards = (cards) => {
  const seen = new Set();
  const result = [];
  for (const card of cards) {
    const key = `${normalizarComercio(card?.codigo_comercio)}-${card?.tbk_user}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(card);
  }
  return result;
};

// ---------------------------------------------------------------------------
// Resolución del comercio con el que inscribir una tarjeta desde la Billetera
// (Perfil), donde NO hay máquina seleccionada y por tanto
// `/apiV2/webpay/onepay/0` es la única fuente del `codigo_comercio`.
//
// Vive acá, y no en el componente, porque su valor está en poder distinguir
// las formas de fallo en Sentry: `ApiCore.get` devuelve `respApiData.data ||
// respApiData`, así que un `{status:"error"}` del backend (HTTP 200, ver
// ADR-0004) llega como objeto y no como lista — indistinguible de "no hay
// comercios configurados" si sólo se mira `Array.isArray`. Cuando el fallo se
// tragaba en silencio, en Sentry quedaba el bloqueo al usuario
// (OPENMATIC-WEB-DB, 11 eventos en producción) sin ningún rastro de su causa.

/**
 * motivoComercioNoResuelto(data)
 * @returns `null` si `data` sirve para inscribir; si no, el motivo:
 *   "backend_respondio_error" | "respuesta_no_es_lista" |
 *   "sin_comercios_configurados" | "comercio_sin_codigo".
 * Nunca lanza.
 */
export const motivoComercioNoResuelto = (data) => {
  if (!Array.isArray(data)) {
    return data && typeof data === "object" && data.status === "error"
      ? "backend_respondio_error"
      : "respuesta_no_es_lista";
  }
  if (data.length === 0) return "sin_comercios_configurados";
  if (normalizarComercio(data[0]?.oneclick_codigo_comercio) === "") {
    return "comercio_sin_codigo";
  }
  return null;
};
